import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Play, Pause, MapPin, ExternalLink, BookOpen, Calendar, VolumeX, SkipBack, SkipForward, Loader2, RefreshCw, Share2, ArrowUp, Heart, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import BurgerMenu from './BurgerMenu';
import BackButton from './BackButton';
import { SUPABASE_CONFIG } from '../config/supabase';
import tourService from '../services/tourService';
import { useDistanceUnit } from '../contexts/DistanceUnitContext';
import { useLocationCoords } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';

// Simple markdown renderer for tour content
const renderMarkdown = (text: string, onCitationClick?: (index: number) => void): React.ReactNode => {
  if (!text) return null;
  
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let currentParagraph: string[] = [];
  
  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const paragraphText = currentParagraph.join(' ');
      elements.push(
        <p key={elements.length} style={{ marginBottom: '16px', lineHeight: '1.8' }}>
          {renderInlineMarkdown(paragraphText)}
        </p>
      );
      currentParagraph = [];
    }
  };
  
  const renderInlineMarkdown = (line: string): React.ReactNode => {
    // Handle bold **text** and __text__ AND citations [1], [2], etc.
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let keyIndex = 0;
    
    while (remaining.length > 0) {
      // Check for citations [1], [2], [3] etc. OR bold
      const citationMatch = remaining.match(/\[(\d+)\]/);
      const boldMatch = remaining.match(/\*\*(.+?)\*\*|__(.+?)__/);
      
      // Find which comes first
      const citationIndex = citationMatch?.index ?? Infinity;
      const boldIndex = boldMatch?.index ?? Infinity;
      
      if (citationIndex < boldIndex && citationMatch && citationMatch.index !== undefined) {
        // Citation comes first
        if (citationMatch.index > 0) {
          parts.push(remaining.substring(0, citationMatch.index));
        }
        const citationNum = parseInt(citationMatch[1], 10);
        parts.push(
          <span
            key={keyIndex++}
            onClick={() => onCitationClick?.(citationNum - 1)}
            style={{
              fontSize: '11px',
              color: 'var(--primary-color)',
              cursor: 'pointer',
              verticalAlign: 'super',
              fontWeight: '500',
              opacity: 0.8,
              transition: 'opacity 0.2s',
              padding: '0 1px'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.8'; }}
            title={`View source ${citationNum}`}
          >
            [{citationNum}]
          </span>
        );
        remaining = remaining.substring(citationMatch.index + citationMatch[0].length);
      } else if (boldIndex < citationIndex && boldMatch && boldMatch.index !== undefined) {
        // Bold comes first
        if (boldMatch.index > 0) {
          parts.push(remaining.substring(0, boldMatch.index));
        }
        parts.push(
          <strong key={keyIndex++}>{boldMatch[1] || boldMatch[2]}</strong>
        );
        remaining = remaining.substring(boldMatch.index + boldMatch[0].length);
      } else {
        // No more matches, add remaining text
        parts.push(remaining);
        break;
      }
    }
    
    return parts.length === 1 ? parts[0] : <>{parts}</>;
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    // Empty line - flush paragraph
    if (trimmedLine === '') {
      flushParagraph();
      continue;
    }
    
    // Headers
    if (trimmedLine.startsWith('### ')) {
      flushParagraph();
      elements.push(
        <h4 key={elements.length} style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginTop: '24px', 
          marginBottom: '12px',
          color: 'var(--text-primary)'
        }}>
          {trimmedLine.replace(/^### /, '')}
        </h4>
      );
      continue;
    }
    
    if (trimmedLine.startsWith('## ')) {
      flushParagraph();
      elements.push(
        <h3 key={elements.length} style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          marginTop: '28px', 
          marginBottom: '14px',
          color: 'var(--primary-color)'
        }}>
          {trimmedLine.replace(/^## /, '')}
        </h3>
      );
      continue;
    }
    
    if (trimmedLine.startsWith('# ')) {
      flushParagraph();
      elements.push(
        <h2 key={elements.length} style={{ 
          fontSize: '20px', 
          fontWeight: '700', 
          marginTop: '32px', 
          marginBottom: '16px',
          color: 'var(--text-primary)'
        }}>
          {trimmedLine.replace(/^# /, '')}
        </h2>
      );
      continue;
    }
    
    // Bullet points
    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      flushParagraph();
      elements.push(
        <div key={elements.length} style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '8px',
          paddingLeft: '8px'
        }}>
          <span style={{ color: 'var(--primary-color)' }}>•</span>
          <span style={{ lineHeight: '1.6' }}>{renderInlineMarkdown(trimmedLine.substring(2))}</span>
        </div>
      );
      continue;
    }
    
    // Numbered lists
    const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)/);
    if (numberedMatch) {
      flushParagraph();
      elements.push(
        <div key={elements.length} style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '8px',
          paddingLeft: '8px'
        }}>
          <span style={{ color: 'var(--primary-color)', fontWeight: '600', minWidth: '20px' }}>
            {numberedMatch[1]}.
          </span>
          <span style={{ lineHeight: '1.6' }}>{renderInlineMarkdown(numberedMatch[2])}</span>
        </div>
      );
      continue;
    }
    
    // Regular text - add to current paragraph
    currentParagraph.push(trimmedLine);
  }
  
  // Flush any remaining paragraph
  flushParagraph();
  
  return elements;
};

const Tour: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: tourIdFromUrl } = useParams<{ id: string }>();
  const { formatDistance } = useDistanceUnit();
  const { latitude: userLatitude, longitude: userLongitude } = useLocationCoords();
  const { user } = useAuth();
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [timeRemaining, setTimeRemaining] = useState(45); // minutes
  const [isPlaying, setIsPlaying] = useState(false);
  const [mapUrl, setMapUrl] = useState<string | null>(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const [loadingTour, setLoadingTour] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [tourData, setTourData] = useState<any>(null);
  const [tourLocation, setTourLocation] = useState<string>('');
  const [distanceFromUser, setDistanceFromUser] = useState<number | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const sourceRefs = React.useRef<(HTMLAnchorElement | null)[]>([]);
  
  // Audio player state
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioStatus, setAudioStatus] = useState<'pending' | 'processing' | 'completed' | 'failed'>('pending');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false); // Toggle between button and player
  const [saveCount, setSaveCount] = useState(0);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate distance from user to tour location
  useEffect(() => {
    if (userLatitude && userLongitude && tourData?.locationData?.latitude && tourData?.locationData?.longitude) {
      const distance = calculateDistance(
        userLatitude,
        userLongitude,
        tourData.locationData.latitude,
        tourData.locationData.longitude
      );
      setDistanceFromUser(distance);
    }
  }, [userLatitude, userLongitude, tourData]);

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Handle citation click - scroll to the source
  const handleCitationClick = (index: number) => {
    const sourceElement = sourceRefs.current[index];
    if (sourceElement) {
      sourceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight briefly
      sourceElement.style.transform = 'scale(1.02)';
      sourceElement.style.borderColor = 'var(--primary-color)';
      sourceElement.style.boxShadow = '0 4px 20px rgba(99, 102, 241, 0.3)';
      setTimeout(() => {
        sourceElement.style.transform = '';
        sourceElement.style.borderColor = '';
        sourceElement.style.boxShadow = '';
      }, 2000);
    }
  };

  // Load tour data either from navigation state or from database
  useEffect(() => {
    const stateData = location.state as any;
    
    if (stateData?.tourData) {
      // Tour data passed via navigation
      console.log('📍 Tour data from navigation:', stateData.tourData);
      console.log('👤 Creator info from navigation:', stateData.tourData.creator);
      
      // Check if this is a pending tour
      if (stateData.tourData.researchStatus === 'pending' || !stateData.tourData.narration || stateData.tourData.narration === '') {
        console.log('⏳ Pending tour detected from navigation state, redirecting');
        setIsRedirecting(true);
        
        // Check localStorage for generation state
        const storedPendingTour = localStorage.getItem('pendingTourGeneration');
        if (storedPendingTour) {
          const pendingData = JSON.parse(storedPendingTour);
          if (pendingData.tourId === stateData.tourData.tourId) {
            setTimeout(() => {
              navigate('/generating', {
                replace: true,
                state: {
                  coordinates: pendingData.coordinates,
                  interests: pendingData.interests,
                  geocodeData: pendingData.geocodeData,
                  selectedPlaces: pendingData.selectedPlaces,
                  nearbyPlaces: pendingData.nearbyPlaces
                }
              });
            }, 100);
            return;
          }
        }
        
        // If no generation state found, redirect to collection
        setTimeout(() => {
          navigate('/past-tours', { replace: true });
        }, 100);
        return;
      }
      
      setTourData(stateData.tourData);
      setTourLocation(stateData.location || '');
      // Set initial audio state from tour data
      setAudioStatus(stateData.tourData.audioStatus || 'pending');
      setAudioUrl(stateData.tourData.audioUrl || null);
      // Set save count
      setSaveCount(stateData.tourData.saveCount || 0);
      
      // IMPORTANT: Fetch fresh audio status from database to handle cases where:
      // - User triggered generation then navigated away
      // - Audio generation completed while user was on another page
      const refreshAudioStatus = async () => {
        try {
          const freshTour = await tourService.getTour(stateData.tourData.tourId);
          console.log('🔄 Refreshed audio status:', freshTour.audioStatus);
          setAudioStatus(freshTour.audioStatus || 'pending');
          setAudioUrl(freshTour.audioUrl || null);
        } catch (err) {
          console.error('Failed to refresh audio status:', err);
        }
      };
      refreshAudioStatus();
    } else if (tourIdFromUrl) {
      // Load tour from database using URL parameter
      loadTourFromDatabase(tourIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourIdFromUrl, location.state]);

  const loadTourFromDatabase = async (tourId: string) => {
    setLoadingTour(true);
    try {
      const tour = await tourService.getTour(tourId);
      console.log('📍 Tour data loaded:', tour);
      console.log('👤 Creator info:', tour.creator);
      
      // Check if tour is still pending - redirect to generating screen
      if ((tour as any).researchStatus === 'pending') {
        console.log('⏳ Tour is pending, redirecting to generating screen');
        
        // Set redirecting state to prevent showing error content
        setIsRedirecting(true);
        
        // Check localStorage for generation state
        const storedPendingTour = localStorage.getItem('pendingTourGeneration');
        if (storedPendingTour) {
          const pendingData = JSON.parse(storedPendingTour);
          if (pendingData.tourId === tourId) {
            // Navigate back to generating screen with state
            setTimeout(() => {
              navigate('/generating', {
                replace: true,
                state: {
                  coordinates: pendingData.coordinates,
                  interests: pendingData.interests,
                  geocodeData: pendingData.geocodeData,
                  selectedPlaces: pendingData.selectedPlaces,
                  nearbyPlaces: pendingData.nearbyPlaces
                }
              });
            }, 100); // Small delay to ensure state is set
            return;
          }
        }
        
        // If no generation state found, redirect to collection
        setTimeout(() => {
          navigate('/past-tours', { replace: true });
        }, 100);
        return;
      }
      
      // Only set tour data if it's completed
      setTourData(tour);
      setTourLocation(tour.locationData?.formattedAddress || '');
      // Set audio state from tour data
      setAudioStatus(tour.audioStatus || 'pending');
      setAudioUrl(tour.audioUrl || null);
      // Set save count from tour data
      setSaveCount((tour as any).saveCount || 0);
      setLoadingTour(false);
    } catch (error) {
      console.error('Failed to load tour:', error);
      setLoadingTour(false);
      // Show error or redirect
      navigate('/home');
    }
  };

  // Poll for audio status when it's processing
  useEffect(() => {
    if (audioStatus === 'processing' && tourData?.tourId) {
      console.log('🎙️ Starting audio status polling...');
      
      pollingRef.current = setInterval(async () => {
        try {
          const updatedTour = await tourService.getTour(tourData.tourId);
          console.log('🔄 Audio status:', updatedTour.audioStatus);
          
          if (updatedTour.audioStatus === 'completed' && updatedTour.audioUrl) {
            setAudioStatus('completed');
            setAudioUrl(updatedTour.audioUrl);
            setIsRetrying(false);
            // Don't auto-open player - wait for user to click
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          } else if (updatedTour.audioStatus === 'failed') {
            setAudioStatus('failed');
            setAudioError('Audio generation failed');
            setIsRetrying(false);
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }
        } catch (err) {
          console.error('Error polling audio status:', err);
        }
      }, 3000); // Poll every 3 seconds

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      };
    }
  }, [audioStatus, tourData?.tourId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Audio event handlers
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsAudioReady(true);
      setAudioLoading(false);
    }
  }, []);

  const handleAudioError = useCallback(() => {
    setAudioError('Failed to load audio');
    setAudioLoading(false);
    setIsAudioReady(false);
  }, []);

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  }, []);

  // Initialize audio element when URL is available
  useEffect(() => {
    if (audioUrl && !audioRef.current) {
      setAudioLoading(true);
      const audio = new Audio(audioUrl);
      audio.preload = 'metadata';
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('error', handleAudioError);
      audio.addEventListener('ended', handleAudioEnded);
      audioRef.current = audio;
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        audioRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audioRef.current.removeEventListener('error', handleAudioError);
        audioRef.current.removeEventListener('ended', handleAudioEnded);
        audioRef.current = null;
      }
    };
  }, [audioUrl, handleTimeUpdate, handleLoadedMetadata, handleAudioError, handleAudioEnded]);

  // Audio control functions
  const togglePlayPause = useCallback(async () => {
    console.log('🎵 togglePlayPause called', { audioStatus, tourId: tourData?.tourId, isRetrying });
    
    // If audio is pending, trigger generation first
    if (audioStatus === 'pending') {
      console.log('🎙️ Audio is pending, triggering generation...');
      
      if (!tourData?.tourId) {
        console.error('❌ No tourId available');
        return;
      }
      
      if (isRetrying) {
        console.log('⏳ Already retrying, skipping...');
        return;
      }
      
      setIsRetrying(true);
      setAudioError(null);
      setAudioStatus('processing');
      
      try {
        console.log('📡 Calling tourService.generateAudio with tourId:', tourData.tourId);
        await tourService.generateAudio(tourData.tourId);
        console.log('✅ Audio generation request sent successfully');
        // The polling will pick up when it's ready and auto-play
      } catch (err) {
        console.error('❌ Failed to generate audio:', err);
        setAudioError('Failed to generate audio');
        setAudioStatus('failed');
        setIsRetrying(false);
      }
      return;
    }

    // Normal play/pause behavior when audio is ready
    if (!audioRef.current || !isAudioReady) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.error('Failed to play audio:', err);
        setAudioError('Failed to play audio');
      });
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, isAudioReady, audioStatus, tourData?.tourId, isRetrying]);


  const seekTo = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const skipForward = useCallback(() => {
    if (audioRef.current) {
      const newTime = Math.min(audioRef.current.currentTime + 15, duration);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [duration]);

  const skipBackward = useCallback(() => {
    if (audioRef.current) {
      const newTime = Math.max(audioRef.current.currentTime - 15, 0);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, []);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    seekTo(newTime);
  }, [duration, seekTo]);

  const handleProgressHover = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const previewTime = percentage * duration;
    setHoverTime(previewTime);
    setHoverPosition(percentage * 100);
  }, [duration]);

  const handleProgressLeave = useCallback(() => {
    setHoverTime(null);
    setHoverPosition(null);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!duration) return;
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const previewTime = percentage * duration;
    setHoverTime(previewTime);
    setHoverPosition(percentage * 100);
  }, [duration]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!duration || !isDragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const previewTime = percentage * duration;
    setHoverTime(previewTime);
    setHoverPosition(percentage * 100);
  }, [duration, isDragging]);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!duration || !isDragging) return;
    setIsDragging(false);
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.changedTouches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;
    seekTo(newTime);
    // Clear preview after a short delay
    setTimeout(() => {
      setHoverTime(null);
      setHoverPosition(null);
    }, 100);
  }, [duration, isDragging, seekTo]);

  const retryAudioGeneration = useCallback(async () => {
    if (!tourData?.tourId || isRetrying) return;
    
    setIsRetrying(true);
    setAudioError(null);
    setAudioStatus('processing');
    
    try {
      await tourService.generateAudio(tourData.tourId);
      // The polling will pick up when it's ready
    } catch (err) {
      console.error('Failed to retry audio generation:', err);
      setAudioError('Failed to generate audio');
      setAudioStatus('failed');
    } finally {
      setIsRetrying(false);
    }
  }, [tourData?.tourId, isRetrying]);

  // Share tour using native share API
  const handleShareTour = async () => {
    if (!tourData) return;
    
    const address = tourLocation || 'this location';
    
    // Format share text as requested: title on first line, description on second
    const shareText = `${tourData.title}\nCheck out this tour on ${address}`;
    
    try {
      // Use the SocialShare.png image for sharing
      let shareImageFile: File | null = null;
      
      try {
        const shareImageResponse = await fetch('/SocialShare.png');
        if (shareImageResponse.ok) {
          const shareImageBlob = await shareImageResponse.blob();
          shareImageFile = new File([shareImageBlob], 'tour.png', { 
            type: 'image/png'
          });
        }
      } catch (imageError) {
        console.log('Could not fetch share image:', imageError);
      }
      
      // Try to share with Web Share API
      if (navigator.share) {
        const shareData: ShareData = {
          text: shareText,
          url: window.location.href
        };
        
        // Add social share image if available and browser supports file sharing
        if (shareImageFile && navigator.canShare) {
          const filesShareData = { ...shareData, files: [shareImageFile] };
          
          // Check if browser can share files
          if (navigator.canShare(filesShareData)) {
            await navigator.share(filesShareData);
            return;
          }
        }
        
        // Share without image if file sharing not supported
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        const fullShareText = `${shareText}\n\n${window.location.href}`;
        await navigator.clipboard.writeText(fullShareText);
        alert('Tour details copied to clipboard!');
      }
    } catch (err) {
      // User cancelled or error occurred
      if ((err as Error).name !== 'AbortError') {
        console.log('Error sharing:', err);
      }
    }
  };

  // Scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Open Google Maps with the tour location
  const handleMapClick = () => {
    if (!tourData?.locationData) return;
    
    const { latitude, longitude } = tourData.locationData;
    
    // Use coordinates if available, otherwise use the formatted address
    let mapsUrl: string;
    if (latitude && longitude) {
      mapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    } else if (tourLocation) {
      mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(tourLocation)}`;
    } else {
      return; // No location data available
    }
    
    window.open(mapsUrl, '_blank');
  };

  // Handle save/unsave tour
  const handleToggleSave = async () => {
    if (!tourData?.tourId || !user?.id || isSaving) return;
    
    setIsSaving(true);
    try {
      if (isSaved) {
        const result = await tourService.unsaveTour(tourData.tourId);
        if (result.success) {
          setIsSaved(false);
          setSaveCount(prev => Math.max(0, prev - 1)); // Optimistic UI update
        }
      } else {
        const result = await tourService.saveTour(tourData.tourId, user.id);
        if (result.success) {
          setIsSaved(true);
          setSaveCount(prev => prev + 1); // Optimistic UI update
        }
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer for countdown (called before any conditional returns)
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          navigate('/past-tours');
          return 0;
        }
        return prev - 1;
      });
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, [navigate]);

  // Fetch map URL when component mounts or location changes (called before any conditional returns)
  useEffect(() => {
    if (tourLocation) {
      fetchMapUrl(tourLocation);
    }
  }, [tourLocation]);

  // Check if user came from home screen
  const fromHome = (location.state as any)?.fromHome;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isNearbyTour = (location.state as any)?.isNearbyTour;

  // Check if this tour was created by another user (not the current user)
  const isOtherUsersTour = tourData?.userId && user?.id && tourData.userId !== user.id;

  // Check if tour is saved when tour data loads
  useEffect(() => {
    const checkIfSaved = async () => {
      // Only check if this is another user's tour
      if (tourData?.tourId && user && isOtherUsersTour) {
        const saved = await tourService.isTourSaved(tourData.tourId);
        setIsSaved(saved);
      }
    };
    checkIfSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourData?.tourId, user?.id, isOtherUsersTour]);

  // Get citations/sources from tour data
  const sources: string[] = tourData?.sources || [];

  // Use actual tour data or fallback to defaults
  const tourInfo = tourData ? {
    name: tourData.title,
    description: tourData.description,
    distance: tourData.distance,
    duration: tourData.estimatedDuration
  } : {
    name: 'Historical Downtown Walk',
    description: 'Explore the city\'s founding stories and colonial architecture',
    distance: '0.8 mi',
    duration: 45
  };

  const currentLocation = tourLocation || '45 Smith Street, New York, NY';

  // Shorten location by removing country, state/sub-area, and zip code
  const shortenLocation = (location: string): string => {
    if (!location) return '';
    
    // Split by comma and take only the first 1-2 parts (street + city)
    const parts = location.split(',').map(p => p.trim());
    
    if (parts.length <= 2) {
      // Already short, just return first part or both
      return parts[0];
    }
    
    // Take first two parts (usually street and city/neighborhood)
    // Remove any zip codes from the second part
    const firstPart = parts[0];
    let secondPart = parts[1];
    
    // Remove zip codes (5 digits or 5+4 format)
    secondPart = secondPart.replace(/\s*\d{5}(-\d{4})?\s*/, '').trim();
    
    // If second part is just a state abbreviation or empty, skip it
    if (secondPart.length <= 3) {
      return firstPart;
    }
    
    return `${firstPart}, ${secondPart}`;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const shortLocation = shortenLocation(currentLocation);

  // Function to fetch map URL from server
  const fetchMapUrl = async (address: string) => {
    try {
      setMapLoading(true);
      setMapError(null);

      const response = await fetch(`${SUPABASE_CONFIG.url}${SUPABASE_CONFIG.endpoints.getMapUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_CONFIG.anonKey}`,
          'apikey': SUPABASE_CONFIG.anonKey
        },
        body: JSON.stringify({ address })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch map URL: ${response.status}`);
      }

      const data = await response.json();
      setMapUrl(data.mapUrl);
    } catch (error) {
      console.error('Error fetching map URL:', error);
      setMapError(error instanceof Error ? error.message : 'Failed to load map');
    } finally {
      setMapLoading(false);
    }
  };

  const tourContent = tourData?.narration || `Welcome to your walking tour! We're sorry, but it seems the tour content didn't load properly. This might be because you're viewing this page directly without going through the tour generation process.

To experience a full tour:
1. Go back to the home page
2. Click "Craft New Tour"
3. Allow location access or enter your location
4. Select your interests
5. Wait for the tour to generate

Your personalized tour will include fascinating historical stories, architectural insights, and local legends tailored to your interests and location.`;

  // Show loading state while fetching tour from database
  // Show loading screen while redirecting to prevent showing error content
  if (isRedirecting || loadingTour) {
    return (
      <div className="app">
        <div className="header">
          <BackButton onClick={() => navigate(fromHome ? '/home' : '/past-tours')} />
          <h3 className="header-title">Loading Tour</h3>
          <BurgerMenu />
        </div>
        <div className="container flex-center" style={{ height: '50vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
            <p style={{ color: 'var(--text-secondary)' }}>
              {isRedirecting ? 'Redirecting to tour progress...' : 'Loading your tour...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Prepare SEO data
  const tourTitle = tourData?.title || 'Tour';
  const tourDescription = tourData?.description || 'Explore this amazing walking tour';
  const tourLocationForSEO = tourData?.locationData?.formattedAddress || tourLocation || '';
  const shareUrl = tourData?.tourId 
    ? `https://miaitor.com/tour/${tourData.tourId}` 
    : 'https://miaitor.com';
  const shareImage = mapUrl || 'https://miaitor.com/SocialShare.png';
  
  // Create comprehensive tour description for SEO
  const fullDescription = tourLocationForSEO 
    ? `${tourDescription} - ${tourLocationForSEO}` 
    : tourDescription;
  
  // Create JSON-LD structured data for search engines
  const tourJsonLd = tourData ? {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": tourTitle,
    "description": tourDescription,
    "touristType": "Everyone",
    "itinerary": {
      "@type": "ItemList",
      "numberOfItems": sources.length,
      "itemListElement": sources.map((source: string, index: number) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": source
      }))
    },
    "provider": {
      "@type": "Organization",
      "name": "Miaitor",
      "url": "https://miaitor.com",
      "logo": "https://miaitor.com/logo512.png"
    },
    "location": tourData.locationData ? {
      "@type": "Place",
      "name": tourLocationForSEO,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": tourLocationForSEO
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": tourData.locationData.latitude,
        "longitude": tourData.locationData.longitude
      }
    } : undefined,
    "creator": tourData.creator ? {
      "@type": "Person",
      "name": tourData.creator.name || "Miaitor User"
    } : undefined,
    "dateCreated": tourData.createdAt ? new Date(tourData.createdAt).toISOString() : undefined,
    "interactionStatistic": {
      "@type": "InteractionCounter",
      "interactionType": "https://schema.org/LikeAction",
      "userInteractionCount": saveCount || 0
    },
    "duration": tourData.estimatedDuration ? `PT${tourData.estimatedDuration}M` : undefined,
    "distance": tourData.distance || undefined
  } : null;

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{tourTitle} | Miaitor</title>
        <meta name="title" content={`${tourTitle} | Miaitor`} />
        <meta name="description" content={fullDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={tourTitle} />
        <meta property="og:description" content={fullDescription} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Map of ${tourTitle}`} />
        <meta property="og:site_name" content="Miaitor" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={tourTitle} />
        <meta name="twitter:description" content={fullDescription} />
        <meta name="twitter:image" content={shareImage} />
        <meta name="twitter:image:alt" content={`Map of ${tourTitle}`} />
        
        {/* Additional Meta Tags */}
        <meta name="author" content={tourData?.creator?.name || "Miaitor"} />
        {tourLocationForSEO && <meta name="geo.region" content={tourLocationForSEO.split(',').pop()?.trim()} />}
        {tourLocationForSEO && <meta name="geo.placename" content={tourLocationForSEO} />}
        {tourData?.locationData?.latitude && (
          <meta name="geo.position" content={`${tourData.locationData.latitude};${tourData.locationData.longitude}`} />
        )}
        {tourData?.locationData && (
          <meta name="ICBM" content={`${tourData.locationData.latitude}, ${tourData.locationData.longitude}`} />
        )}
        
        {/* Canonical URL */}
        <link rel="canonical" href={shareUrl} />
        
        {/* JSON-LD Structured Data */}
        {tourJsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(tourJsonLd)}
          </script>
        )}
      </Helmet>

      <div className="app">
        <div className="header">
          <BackButton onClick={() => navigate(fromHome ? '/' : '/past-tours')} />
        <h3 className="header-title">
          {isOtherUsersTour ? 'Community Tour' : 'Your Tour'}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Heart icon - only show for tours created by other users */}
          {isOtherUsersTour && user && (
            <motion.div
              onClick={handleToggleSave}
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.05 }}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                backgroundColor: 'var(--secondary-color)',
                flexShrink: 0,
                opacity: isSaving ? 0.6 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              <Heart 
                size={20} 
                strokeWidth={2}
                stroke={isSaved ? 'var(--primary-color)' : 'var(--text-secondary)'}
                fill={isSaved ? 'var(--primary-color)' : 'none'}
                style={{ transition: 'all 0.2s ease' }}
              />
            </motion.div>
          )}
          <BurgerMenu />
        </div>
      </div>

      <div className="container">
        {/* Tour Information Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            padding: '16px',
            backgroundColor: 'var(--card-background)',
            borderRadius: '16px',
            boxShadow: 'var(--shadow-sm)',
            marginBottom: '24px'
          }}
        >
          {/* Google Maps Screenshot */}
          <div 
            onClick={handleMapClick}
            style={{ 
              width: '100%',
              height: '120px',
              backgroundColor: 'var(--secondary-color)',
              borderRadius: '12px',
              marginBottom: '16px',
              backgroundImage: mapUrl ? `url("${mapUrl}")` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              border: '1px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              cursor: mapUrl ? 'pointer' : 'default',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (mapUrl) {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (mapUrl) {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {mapLoading ? (
              <div style={{ 
                textAlign: 'center', 
                color: 'var(--text-secondary)',
                fontSize: '14px'
              }}>
                <MapPin size={20} style={{ marginBottom: '4px' }} />
                <div>Loading map...</div>
              </div>
            ) : mapError ? (
              <div style={{ 
                textAlign: 'center', 
                color: 'var(--text-secondary)',
                backgroundColor: 'var(--background)',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                <MapPin size={20} style={{ marginBottom: '4px' }} />
                <div>{currentLocation}</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                  Map unavailable
                </div>
              </div>
            ) : mapUrl ? (
              // Overlay with location info when map is loaded
              <div style={{
                position: 'absolute',
                bottom: '8px',
                left: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#333',
                backdropFilter: 'blur(4px)'
              }}>
                <MapPin size={14} style={{ marginRight: '4px', verticalAlign: 'text-bottom' }} />
                {currentLocation.length > 30 ? `${currentLocation.substring(0, 30)}...` : currentLocation}
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                color: 'var(--text-secondary)',
                backgroundColor: 'var(--background)',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                <MapPin size={20} style={{ marginBottom: '4px' }} />
                <div>{currentLocation}</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                  No map available
                </div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '8px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: 0 }}>{tourInfo.name}</h3>
          </div>
          
          <p style={{ 
            fontSize: '14px', 
            color: 'var(--text-secondary)',
            marginBottom: '12px',
            lineHeight: '1.4'
          }}>
            {tourInfo.description}
          </p>
          
          <div style={{ display: 'flex', gap: '16px', color: 'var(--text-secondary)' }}>
            {distanceFromUser !== null && (
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <MapPin size={16} />
                <span style={{ fontSize: '14px' }}>{formatDistance(distanceFromUser)}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <Calendar size={16} />
              <span style={{ fontSize: '14px' }}>
                {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '/')}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <Heart size={16} />
              <span style={{ fontSize: '14px' }}>{(saveCount || 0).toLocaleString()}</span>
            </div>
          </div>
        </motion.div>

        {/* Play Tour Button */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          onClick={async () => {
            console.log('🎵 Play/Close Tour Button clicked!', { showPlayer, audioStatus, tourId: tourData?.tourId });
            
            // If player is already showing, hide it
            if (showPlayer) {
              console.log('🚪 Closing player, isPlaying:', isPlaying);
              setShowPlayer(false);
              // Pause audio if playing
              if (audioRef.current) {
                if (!audioRef.current.paused) {
                  console.log('⏸️ Pausing audio');
                  audioRef.current.pause();
                }
                setIsPlaying(false);
              }
              return;
            }
            
            // If audio is pending or failed, trigger generation
            if (audioStatus === 'pending' || audioStatus === 'failed') {
              if (!tourData?.tourId || isRetrying) return;
              
              setIsRetrying(true);
              setAudioError(null);
              setAudioStatus('processing');
              // Don't show player during generation
              
              try {
                console.log('📡 Triggering audio generation...');
                await tourService.generateAudio(tourData.tourId);
                console.log('✅ Audio generation request sent');
                // Note: isRetrying will be reset by the polling mechanism when status changes
              } catch (err) {
                console.error('❌ Failed to generate audio:', err);
                setAudioError('Failed to generate audio');
                setAudioStatus('failed');
                setIsRetrying(false);
              }
            } else if (audioStatus === 'completed') {
              // If audio is ready, show player and play
              setShowPlayer(true);
              setTimeout(() => togglePlayPause(), 100);
            }
          }}
          disabled={isRetrying && audioStatus === 'processing'}
          whileHover={!(isRetrying && audioStatus === 'processing') ? { scale: 1.02 } : {}}
          whileTap={!(isRetrying && audioStatus === 'processing') ? { scale: 0.98 } : {}}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '18px 24px',
            backgroundColor: 'var(--primary-color)',
            color: 'white',
            border: 'none',
            borderRadius: '16px',
            fontSize: '18px',
            fontWeight: '600',
            cursor: (isRetrying && audioStatus === 'processing') ? 'not-allowed' : 'pointer',
            marginBottom: '24px',
            opacity: (isRetrying && audioStatus === 'processing') ? 0.7 : 1,
            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
            transition: 'all 0.2s ease'
          }}
        >
          {audioStatus === 'processing' ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Loader2 size={24} />
              </motion.div>
              Generating Audio Tour
            </>
          ) : showPlayer ? (
            <>
              <X size={24} />
              Close Audio Tour
            </>
          ) : (
            <>
              <Play size={24} />
              Play Audio Tour
            </>
          )}
        </motion.button>

        {/* Audio Player */}
        <AnimatePresence mode="wait">
          {showPlayer && (
            <motion.div
              key="audio-player"
              initial={{ opacity: 0, y: 10, scaleY: 0.95 }}
              animate={{ opacity: 1, y: 0, scaleY: 1 }}
              exit={{ opacity: 0, y: -10, scaleY: 0.95 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              style={{
                backgroundColor: 'var(--card-background)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '24px',
                boxShadow: 'var(--shadow-sm)',
                border: '1px solid var(--border-color)',
                transformOrigin: 'top'
              }}
            >
              {/* Audio Status Messages */}
              {audioStatus === 'failed' && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '12px',
                    marginBottom: '16px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <VolumeX size={20} color="var(--error-color)" />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                        Audio Unavailable
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {audioError || 'Could not generate audio'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={retryAudioGeneration}
                    disabled={isRetrying}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: isRetrying ? 'not-allowed' : 'pointer',
                      opacity: isRetrying ? 0.7 : 1
                    }}
                  >
                    <RefreshCw size={14} className={isRetrying ? 'spinning' : ''} />
                    Retry
                  </button>
                </div>
              )}

              {/* Progress Bar (only show when audio is ready) */}
              {audioStatus === 'completed' && audioUrl && (
                <>
                  <div
                    onClick={handleProgressClick}
                    onMouseMove={handleProgressHover}
                    onMouseLeave={handleProgressLeave}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    style={{
                      width: '100%',
                      height: isDragging ? '12px' : '8px', // Larger hit area when dragging
                      backgroundColor: 'var(--border-color)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginBottom: '8px',
                      position: 'relative',
                      overflow: 'visible',
                      touchAction: 'none', // Prevents default touch behaviors
                      transition: 'height 0.2s ease'
                    }}
                  >
                    <motion.div
                      style={{
                        height: '100%',
                        backgroundColor: 'var(--primary-color)',
                        borderRadius: '4px',
                        width: `${duration ? (currentTime / duration) * 100 : 0}%`
                      }}
                      transition={{ duration: 0.1 }}
                    />
                    
                    {/* Hover Timestamp Tooltip */}
                    {hoverTime !== null && hoverPosition !== null && (
                      <>
                        {/* Hover indicator dot */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: `${hoverPosition}%`,
                            transform: 'translate(-50%, -50%)',
                            width: '12px',
                            height: '12px',
                            backgroundColor: 'var(--primary-color)',
                            borderRadius: '50%',
                            border: '2px solid white',
                            pointerEvents: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                            zIndex: 9
                          }}
                        />
                        {/* Tooltip */}
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '16px',
                            left: `${hoverPosition}%`,
                            transform: 'translateX(-50%)',
                            backgroundColor: 'var(--text-primary)',
                            color: 'var(--background)',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                            zIndex: 10
                          }}
                        >
                          {formatTime(Math.floor(hoverTime))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Time Display */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginBottom: '12px',
                    fontFamily: 'monospace'
                  }}>
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </>
              )}

              {/* Player Controls */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px'
              }}>
                {/* Skip Backward */}
                <button
                  onClick={skipBackward}
                  disabled={!isAudioReady}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'var(--secondary-color)',
                    color: isAudioReady ? 'var(--text-primary)' : 'var(--text-secondary)',
                    cursor: isAudioReady ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    opacity: isAudioReady ? 1 : 0.5
                  }}
                >
                  <SkipBack size={18} />
                </button>

                {/* Play/Pause Button */}
                <motion.button
                  onClick={togglePlayPause}
                  disabled={audioStatus === 'processing' || (audioStatus === 'completed' && !isAudioReady)}
                  whileHover={(audioStatus === 'pending' || isAudioReady) ? { scale: 1.05 } : {}}
                  whileTap={(audioStatus === 'pending' || isAudioReady) ? { scale: 0.95 } : {}}
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: audioStatus === 'processing' ? 'var(--secondary-color)' : 'var(--primary-color)',
                    color: (audioStatus === 'pending' || (audioStatus === 'completed' && isAudioReady)) ? 'white' : 'var(--text-secondary)',
                    cursor: audioStatus === 'processing' ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: (audioStatus === 'pending' || (audioStatus === 'completed' && isAudioReady)) ? 
                      '0 4px 16px rgba(99, 102, 241, 0.3)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {audioLoading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      <Loader2 size={28} />
                    </motion.div>
                  ) : isPlaying ? (
                    <Pause size={28} fill="currentColor" />
                  ) : (
                    <Play size={28} fill="currentColor" style={{ marginLeft: '3px' }} />
                  )}
                </motion.button>

                {/* Skip Forward */}
                <button
                  onClick={skipForward}
                  disabled={!isAudioReady}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'var(--secondary-color)',
                    color: isAudioReady ? 'var(--text-primary)' : 'var(--text-secondary)',
                    cursor: isAudioReady ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    opacity: isAudioReady ? 1 : 0.5
                  }}
                >
                  <SkipForward size={18} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div style={{
            backgroundColor: 'var(--card-background)',
            borderRadius: 'var(--border-radius)',
            padding: '24px',
            marginBottom: '24px',
            fontSize: '16px',
            color: 'var(--text-primary)'
          }}>
            {renderMarkdown(tourContent, handleCitationClick)}
          </div>
        </motion.div>

        {/* Creator Information */}
        {tourData?.createdAt && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{
              backgroundColor: 'var(--card-background)',
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--border-color)'
            }}
          >
            {/* Profile Picture */}
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '2px solid var(--primary-color)',
              backgroundColor: 'var(--secondary-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <img
                src={tourData.creator?.avatarUrl || '/PFP.svg'}
                alt={tourData.creator?.name || 'User'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            
            {/* Creator Details */}
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                marginBottom: '2px'
              }}>
                Created by:
              </div>
              <div style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '4px'
              }}>
                {tourData.creator?.name || 'TourBud User'}
              </div>
              <div style={{
                fontSize: '13px',
                color: 'var(--text-secondary)'
              }}>
                {new Date(tourData.createdAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px'
          }}
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleShareTour}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '14px 20px',
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.2)',
              transition: 'all 0.2s ease'
            }}
          >
            <Share2 size={18} />
            Share Tour
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={scrollToTop}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '14px 20px',
              backgroundColor: 'var(--card-background)',
              color: 'var(--text-primary)',
              border: '2px solid var(--border-color)',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <ArrowUp size={18} />
            Back to Top
          </motion.button>
        </motion.div>

        {/* Sources/Citations Section */}
        {sources.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ marginBottom: '80px' }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              color: 'var(--text-secondary)'
            }}>
              <BookOpen size={18} />
              <h3 style={{ 
                fontSize: '16px', 
                fontWeight: '600',
                margin: 0,
                color: 'var(--text-primary)'
              }}>
                Sources ({sources.length})
              </h3>
            </div>
            
            <div 
              id="sources-section"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}
            >
              {sources.map((source, index) => {
                // Extract domain from URL for display
                let domain = '';
                let displayUrl = source;
                try {
                  const url = new URL(source);
                  domain = url.hostname.replace('www.', '');
                  // Truncate long URLs
                  displayUrl = source.length > 60 ? source.substring(0, 60) + '...' : source;
                } catch {
                  domain = 'Source';
                }
                
                return (
                  <a
                    key={index}
                    ref={(el) => { sourceRefs.current[index] = el; }}
                    href={source}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      backgroundColor: 'var(--card-background)',
                      borderRadius: '12px',
                      border: '1px solid var(--border-color)',
                      textDecoration: 'none',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--primary-color)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border-color)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      backgroundColor: 'var(--primary-color)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <span style={{ 
                        color: 'white', 
                        fontSize: '12px', 
                        fontWeight: '600' 
                      }}>
                        {index + 1}
                      </span>
                    </div>
                    
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        marginBottom: '2px'
                      }}>
                        {domain}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: 'var(--text-secondary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {displayUrl}
                      </div>
                    </div>
                    
                    <ExternalLink 
                      size={14} 
                      style={{ 
                        color: 'var(--text-secondary)',
                        flexShrink: 0
                      }} 
                    />
                  </a>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </div>
    </>
  );
};

export default Tour;
